import React from 'react';
import { X, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicy({
  isOpen,
  onClose
}: PrivacyPolicyProps) {
  const {
    t
  } = useLanguage();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-166">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm" onClick={onClose} data-id="element-167" />

      {/* Modal */}
      <div className="relative bg-slate-900 rounded-2xl border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-168">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5 bg-slate-900 sticky top-0" data-id="element-169">
          <div className="flex items-center gap-3" data-id="element-170">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400" data-id="element-171">
              <Shield size={20} data-id="element-172" />
            </div>
            <h2 className="text-2xl font-bold text-white" data-id="element-173">{t.privacy.title}</h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors" data-id="element-174">
            <X size={20} data-id="element-175" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" data-id="element-176">
          <p className="text-slate-400 mb-8" data-id="element-177">
            {t.privacy.lastUpdated}: January 30, 2026
          </p>

          <div className="space-y-8" data-id="element-178">
            {/* Introduction */}
            <section data-id="element-179">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-180">
                {t.privacy.sections.intro.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-181">
                {t.privacy.sections.intro.content}
              </p>
            </section>

            {/* Information We Collect */}
            <section data-id="element-182">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-183">
                {t.privacy.sections.collect.title}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-4" data-id="element-184">
                {t.privacy.sections.collect.content}
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-185">
                {t.privacy.sections.collect.items.map((item, i) => <li key={i} data-id="element-186">{item}</li>)}
              </ul>
            </section>

            {/* How We Use Information */}
            <section data-id="element-187">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-188">
                {t.privacy.sections.use.title}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-4" data-id="element-189">
                {t.privacy.sections.use.content}
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-190">
                {t.privacy.sections.use.items.map((item, i) => <li key={i} data-id="element-191">{item}</li>)}
              </ul>
            </section>

            {/* Data Protection */}
            <section data-id="element-192">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-193">
                {t.privacy.sections.protection.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-194">
                {t.privacy.sections.protection.content}
              </p>
            </section>

            {/* Third Party Services */}
            <section data-id="element-195">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-196">
                {t.privacy.sections.thirdParty.title}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-4" data-id="element-197">
                {t.privacy.sections.thirdParty.content}
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-198">
                {t.privacy.sections.thirdParty.items.map((item, i) => <li key={i} data-id="element-199">{item}</li>)}
              </ul>
            </section>

            {/* Client Projects */}
            <section data-id="element-200">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-201">
                {t.privacy.sections.clientProjects.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-202">
                {t.privacy.sections.clientProjects.content}
              </p>
            </section>

            {/* Cookies */}
            <section data-id="element-203">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-204">
                {t.privacy.sections.cookies.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-205">
                {t.privacy.sections.cookies.content}
              </p>
            </section>

            {/* Your Rights */}
            <section data-id="element-206">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-207">
                {t.privacy.sections.rights.title}
              </h3>
              <p className="text-slate-400 leading-relaxed mb-4" data-id="element-208">
                {t.privacy.sections.rights.content}
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-209">
                {t.privacy.sections.rights.items.map((item, i) => <li key={i} data-id="element-210">{item}</li>)}
              </ul>
            </section>

            {/* Contact */}
            <section data-id="element-211">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-212">
                {t.privacy.sections.contact.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-213">
                {t.privacy.sections.contact.content}
              </p>
              <p className="text-cyan-400 mt-2" data-id="element-214">quickdesigns2026@gmail.com</p>
            </section>

            {/* Changes */}
            <section data-id="element-215">
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-216">
                {t.privacy.sections.changes.title}
              </h3>
              <p className="text-slate-400 leading-relaxed" data-id="element-217">
                {t.privacy.sections.changes.content}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>;
}